import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { completePasswordReset, verifyPasswordResetToken } from "@/modules/auth/api";

type VerificationState = "checking" | "valid" | "invalid";

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get("token")?.trim() ?? "", [searchParams]);
  const [verification, setVerification] = useState<VerificationState>("checking");
  const [email, setEmail] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const passwordsMatch = password === confirmPassword;
  const passwordReady = password.length >= 8 && confirmPassword.length >= 8;

  useEffect(() => {
    let cancelled = false;

    async function verify() {
      if (!token) {
        setVerification("invalid");
        return;
      }
      try {
        const result = await verifyPasswordResetToken(token);
        if (cancelled) return;
        setVerification(result.valid ? "valid" : "invalid");
        setEmail(result.email ?? null);
        setName(result.name ?? null);
      } catch {
        if (!cancelled) setVerification("invalid");
      }
    }

    void verify();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setSubmitted(true);
    if (password.length < 8) {
      setError("Use at least 8 characters for your password.");
      return;
    }
    if (!passwordsMatch) {
      setError("Password confirmation does not match.");
      return;
    }
    setPending(true);
    setError(null);
    try {
      await completePasswordReset({
        token,
        password,
        confirm_password: confirmPassword,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not reset the password.");
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="relative flex min-h-screen flex-col items-center justify-center bg-background p-6">
      <div className="relative w-full max-w-md rounded-card border border-card-border bg-card p-6 shadow-card backdrop-blur-xl md:p-8">
        <p className="text-micro font-semibold uppercase tracking-[0.14em] text-kicker-lux">Password reset</p>
        <h1 className="mt-2 font-sans text-2xl font-bold tracking-tight text-foreground">Choose a new password</h1>

        {verification === "checking" ? (
          <p className="mt-6 rounded-control border border-card-border bg-muted-bg px-3 py-2 text-caption text-muted">
            Checking reset link...
          </p>
        ) : null}

        {verification === "invalid" ? (
          <>
            <p role="alert" className="mt-6 rounded-control border border-error/25 bg-error/5 px-3 py-2 text-caption text-error">
              This password reset link is invalid or has expired.
            </p>
            <Link to="/forgot-password" className="mt-6 block text-center text-caption text-muted transition-colors hover:text-accent">
              Request a new link
            </Link>
          </>
        ) : null}

        {verification === "valid" && submitted ? (
          <>
            <p role="status" className="mt-6 rounded-control border border-success/30 bg-success/10 px-3 py-2 text-caption text-success">
              Password updated. You can sign in now.
            </p>
            <Link to="/login" className="mt-6 block text-center text-caption text-muted transition-colors hover:text-accent">
              Back to sign in
            </Link>
          </>
        ) : null}

        {verification === "valid" && !submitted ? (
          <form className="mt-6 space-y-5" onSubmit={handleSubmit}>
            <div className="rounded-card border border-card-border bg-muted-bg px-4 py-3 text-sm text-foreground">
              <div><span className="font-semibold">Email:</span> {email ?? "-"}</div>
              <div><span className="font-semibold">Name:</span> {name ?? "-"}</div>
            </div>

            <label className="block space-y-2">
              <span className="dl-label text-[0.58rem] text-muted">New password</span>
              <input
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="ui-control w-full"
                autoComplete="new-password"
              />
              {submitted && password.length > 0 && password.length < 8 ? (
                <span className="text-caption text-error">Use at least 8 characters.</span>
              ) : null}
            </label>

            <label className="block space-y-2">
              <span className="dl-label text-[0.58rem] text-muted">Confirm password</span>
              <input
                type="password"
                required
                minLength={8}
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                className="ui-control w-full"
                autoComplete="new-password"
              />
              {submitted && confirmPassword.length > 0 && !passwordsMatch ? (
                <span className="text-caption text-error">Passwords do not match yet.</span>
              ) : null}
            </label>

            {error ? (
              <p role="alert" className="rounded-control border border-error/25 bg-error/5 px-3 py-2 text-caption text-error">{error}</p>
            ) : null}

            <button type="submit" disabled={pending || !passwordReady || !passwordsMatch} className="btn btn-primary w-full">
              {pending ? "Saving..." : "Save password"}
            </button>
          </form>
        ) : null}
      </div>
    </main>
  );
}


